import { computedAsync } from '@vueuse/core'
import type { TilesProvider } from './feature-flags/index.types'
import type { ShortcutItem } from '../components/ShortcutGrid/ShortcutGrid.vue'

export type SponsoredTiles = {
  id: string
  name: string
  image_url: string
  click_url: string
  impression_url: string
  provider?: string
}

type TileProvider = 'admarketplace' | 'affinity' | 'off' | 'direct'

export type SponsoredTilesParams = {
  provider: TileProvider | string
  tilesCount?: number
}

export type MarketPlaceProvider = Record<
  TileProvider | string,
  (tilesCount: number) => Promise<SponsoredTiles[]>
>

const constructShortcutsList = (tiles: SponsoredTiles[]): ShortcutItem[] => {
  return tiles.map((shortcut) => ({
    image: shortcut.image_url,
    name: shortcut.name,
    targetUrl: shortcut.click_url,
    provider: shortcut.provider,
    impressionUrl: shortcut.impression_url,
  }))
}

export const useMarketplaceShortcuts = (
  availableMarketplaceProviders: MarketPlaceProvider,
  featureFlags: ContentDisplaySettings
): {
  provider: string
  shortcuts: Ref<ShortcutItem[]>
  isShortcutsLoading: Ref<boolean>
} => {
  const count = featureFlags.sponsoredTiles?.tilesCount || 3
  const provider = featureFlags.sponsoredTiles?.provider || ''
  let shortcuts: Ref<ShortcutItem[]>
  const isShortcutsLoading = ref(false)

  const fetchTilesFromProvider = async (
    providerName: string | undefined,
    tilesCount: number
  ): Promise<SponsoredTiles[]> => {
    const providerFetcher = providerName
      ? availableMarketplaceProviders[providerName]
      : undefined
    return (await providerFetcher?.(tilesCount)) ?? []
  }

  const accumulateTilesCount = (
    tilesProvider: TilesProvider[]
  ): Record<string, number> => {
    const availableTilesProvider = tilesProvider.filter(
      (tile) => tile.provider !== 'direct'
    )

    return availableTilesProvider.reduce<Record<string, number>>(
      (tilesAccumulator, tilesProviderObject) => {
        const tileProvider = tilesProviderObject.provider
        const tilesCount = tilesProviderObject.tilesCount
        tilesAccumulator[tileProvider] =
          (tilesAccumulator[tileProvider] || 0) + tilesCount

        return tilesAccumulator
      },
      {}
    )
  }

  const removeDuplicateTiles = (
    tilesProvider: Array<SponsoredTiles>
  ): Array<SponsoredTiles> => {
    return tilesProvider.filter(
      (tile, index, tilesArray) =>
        tilesArray.findIndex((tileItem) => tileItem.name === tile.name) ===
        index
    )
  }

  const getProviderTilesOrder = (
    tilesProvider: TilesProvider[]
  ): Array<string> => {
    return Array.from(new Set(tilesProvider.map((item) => item.provider)))
  }

  const getOrderedDynamicTiles = (
    tilesProvider: TilesProvider[],
    deDuplicatedTiles: Array<Array<SponsoredTiles>>
  ): Array<SponsoredTiles> => {
    const providerOrder = getProviderTilesOrder(tilesProvider)

    return tilesProvider.flatMap((tile) => {
      if (tile.provider === 'direct') {
        const { name, click_url, image_url, impression_url } = tile
        return {
          name,
          click_url,
          image_url,
          impression_url,
          id: name,
          provider: 'direct',
        }
      }
      const providerIndex = providerOrder.indexOf(tile.provider)
      const providerTiles = deDuplicatedTiles[providerIndex] ?? []
      const tilesToAdd = providerTiles
        .slice(0, tile.tilesCount)
        .map((addedTile) => ({
          ...addedTile,
          provider: tile.provider,
        }))
      deDuplicatedTiles[providerIndex] = providerTiles.slice(tile.tilesCount)
      return tilesToAdd
    })
  }

  const buildDynamicSponsoredTiles = () => {
    return computedAsync(async () => {
      const tilesProvider =
        featureFlags.dynamicSponsoredTiles?.tilesProvider ?? []

      // Accumulating all the object to find the total tiles for each provider
      const providerTilesCount: Record<string, number> =
        accumulateTilesCount(tilesProvider)

      let tilesProviderResult: SponsoredTiles[][] = [[]]

      const results = await Promise.allSettled(
        Object.entries(providerTilesCount).map(([providerName, tilesCount]) =>
          fetchTilesFromProvider(providerName, tilesCount)
        )
      )
      tilesProviderResult = results
        .filter((result) => result.status === 'fulfilled')
        .map((result) => result.value)
      isShortcutsLoading.value = false
      const orderedTiles: SponsoredTiles[] = getOrderedDynamicTiles(
        tilesProvider,
        tilesProviderResult
      )
      const deDuplicatedTiles: SponsoredTiles[] =
        removeDuplicateTiles(orderedTiles)
      return constructShortcutsList(deDuplicatedTiles)
    }, [])
  }

  const buildSponsoredTiles = () => {
    return computedAsync(async () => {
      let rawTiles: SponsoredTiles[] = []

      try {
        rawTiles = await fetchTilesFromProvider(provider, count)
      } finally {
        isShortcutsLoading.value = false
      }
      return constructShortcutsList(rawTiles)
    }, [])
  }

  if (provider === 'off') {
    return { provider, shortcuts: ref([]), isShortcutsLoading }
  }

  isShortcutsLoading.value = true
  // When the dynamic Sponsored Tiles are set
  if (featureFlags.dynamicSponsoredTiles) {
    shortcuts = buildDynamicSponsoredTiles()
  } else {
    shortcuts = buildSponsoredTiles()
  }

  return {
    provider,
    shortcuts,
    isShortcutsLoading,
  }
}
